import type {Track} from 'livekit-client'
import type {AudioProcessorOptions, TrackProcessor} from 'livekit-client'
import {DEFAULT_THRESHOLD, AUTO_THRESHOLD_MIN, INITIAL_NOISE_FLOOR} from './types'
import {ensureWorkletModule} from './noiseGateWorkletCode'

/**
 * Audio noise gate processor for LiveKit.
 *
 * Routes the mic MediaStreamTrack through an AudioWorkletNode that runs
 * on the dedicated audio rendering thread:
 *   Source → AudioWorkletNode (noise gate) → Destination
 *
 * The worklet thread is **never paused** by the browser when the tab
 * loses visibility, so the gate continues to function in the background.
 *
 * In manual mode the user controls the threshold via a slider.
 * In automatic mode the threshold is computed dynamically from a running
 * estimate of the ambient noise floor (Discord-style adaptive sensitivity).
 *
 * The worklet reports the current RMS level and auto-threshold back to the
 * main thread via MessagePort so the UI can render a real-time audio meter.
 */
export class NoiseGateProcessor implements TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> {
  name = 'noise-gate'

  processedTrack?: MediaStreamTrack

  private audioContext: AudioContext | null = null
  private sourceNode: MediaStreamAudioSourceNode | null = null
  private workletNode: AudioWorkletNode | null = null
  private destinationNode: MediaStreamAudioDestinationNode | null = null

  private threshold = DEFAULT_THRESHOLD
  private manualMode = false

  private _currentLevel = 0
  private _autoThreshold = AUTO_THRESHOLD_MIN
  // Tracks the last noise floor reported by the worklet so we can seed a
  // freshly created worklet after restart() without losing the learned value.
  private _lastNoiseFloor = INITIAL_NOISE_FLOOR

  private onLevelChange: ((level: number) => void) | null = null

  // ---------------------------------------------------------------
  // Public API (called from useAudioSettings / UI)
  // ---------------------------------------------------------------

  /** Set the RMS threshold for manual mode. Values below this are silenced. */
  setThreshold(value: number) {
    this.threshold = value
    this.sendConfig()
  }

  /** Switch between manual (user-set threshold) and automatic (adaptive) mode. */
  setManualMode(manual: boolean) {
    this.manualMode = manual
    this.sendConfig()
  }

  /** Subscribe to real-time level updates (for the audio meter). */
  setLevelCallback(cb: ((level: number) => void) | null) {
    this.onLevelChange = cb
  }

  /** Get the most recent RMS level synchronously. */
  get currentLevel() {
    return this._currentLevel
  }

  /** Get the current auto-computed threshold (for UI display in automatic mode). */
  get autoThreshold() {
    return this._autoThreshold
  }

  /** Reset the noise floor estimate (e.g. when switching microphones). */
  resetNoiseFloor() {
    this._autoThreshold = AUTO_THRESHOLD_MIN
    this._lastNoiseFloor = INITIAL_NOISE_FLOOR
    this.workletNode?.port.postMessage({type: 'reset'})
  }

  // ---------------------------------------------------------------
  // TrackProcessor interface
  // ---------------------------------------------------------------

  async init(opts: AudioProcessorOptions) {
    this.audioContext = opts.audioContext
    await this.buildGraph(opts.track)
  }

  async restart(opts: AudioProcessorOptions) {
    this.teardownGraph()
    // LiveKit does not pass audioContext in restart opts — only in init.
    // Preserve the context from init() so buildGraph() can use it.
    if (opts.audioContext) {
      this.audioContext = opts.audioContext
    }
    await this.buildGraph(opts.track)
  }

  async destroy() {
    this.teardownGraph()
    this.audioContext = null
  }

  // ---------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------

  private sendConfig() {
    this.workletNode?.port.postMessage({
      type: 'config',
      threshold: this.threshold,
      manualMode: this.manualMode,
    })
  }

  private async buildGraph(track: MediaStreamTrack) {
    const ctx = this.audioContext
    if (!ctx) return

    await ensureWorkletModule(ctx)

    this.sourceNode = ctx.createMediaStreamSource(new MediaStream([track]))
    this.workletNode = new AudioWorkletNode(ctx, 'noise-gate')
    this.destinationNode = ctx.createMediaStreamDestination()

    this.sourceNode.connect(this.workletNode)
    this.workletNode.connect(this.destinationNode)

    this.processedTrack = this.destinationNode.stream.getAudioTracks()[0]

    this.workletNode.port.onmessage = (e: MessageEvent) => {
      this._currentLevel = e.data.rms
      this._autoThreshold = e.data.autoThreshold
      this._lastNoiseFloor = e.data.noiseFloor
      this.onLevelChange?.(e.data.rms)
    }

    // Seed the new worklet with the previously learned noise floor so the
    // adaptive threshold doesn't reset to its initial value after mic toggle.
    this.workletNode.port.postMessage({type: 'seed', noiseFloor: this._lastNoiseFloor})
    this.sendConfig()
  }

  private teardownGraph() {
    if (this.workletNode) {
      this.workletNode.port.onmessage = null
      this.workletNode.disconnect()
    }
    this.sourceNode?.disconnect()
    this.destinationNode?.disconnect()

    this.sourceNode = null
    this.workletNode = null
    this.destinationNode = null
    this.processedTrack = undefined
  }
}
